import { CheckCircle, XCircle } from "@phosphor-icons/react";
import type { AudienceReply } from "../../shared/types.ts";
import { replyAuthorDisplay } from "../lib/threads.ts";

const EMPTY_PENDING_REPLY_IDS: ReadonlySet<string> = new Set();

/** Reply list under a thread. Display-only by default; pass onApproveReply (admin)
 *  to surface an inline approve on queued replies. No reply projection (spec). */
export function ThreadReplies({
  replies,
  onApproveReply,
  onHideReply,
  pendingReplyIds = EMPTY_PENDING_REPLY_IDS,
  className = "rt-replies",
}: {
  replies: AudienceReply[];
  onApproveReply?: (replyId: string) => void;
  onHideReply?: (replyId: string) => void;
  pendingReplyIds?: ReadonlySet<string>;
  className?: string;
}) {
  const visible = replies.filter((r) => r.status === "shown" || r.status === "queued");
  if (visible.length === 0) return null;
  return (
    <div className={className}>
      {visible.map((r) => {
        const author = replyAuthorDisplay(r.authorLabel, r.authorIdentityKind);
        const pending = r.status === "queued";
        const replyPending = pendingReplyIds.has(r.id);
        return (
          <div key={r.id} className={`${pending ? "pending" : ""} ${replyPending ? "is-pending" : ""}`}>
            {author.kind !== "hidden" && author.label && (
              <><span className={`who ${author.kind === "anonymous" ? "anon" : ""}`}>{author.label}</span>{" "}</>
            )}
            {r.body}
            {pending && <span className="tag">awaiting approval</span>}
            {pending && onApproveReply && (
              <button
                type="button"
                className="btn good icon"
                title="Approve reply"
                aria-label="Approve reply"
                data-testid="reply-approve-btn"
                disabled={replyPending}
                onClick={() => onApproveReply(r.id)}
              ><CheckCircle weight="fill" /></button>
            )}
            {pending && onHideReply && (
              <button
                type="button"
                className="btn danger icon"
                title="Reject reply"
                aria-label="Reject reply"
                data-testid="reply-hide-btn"
                disabled={replyPending}
                onClick={() => onHideReply(r.id)}
              ><XCircle weight="bold" /></button>
            )}
          </div>
        );
      })}
    </div>
  );
}
